import React, { useMemo, useState } from 'react';
import {
  NotifyChannel,
  ClientPreference,
  defaultNotifyChannel,
  describeEmailStatus,
  openWhatsAppHandoff,
} from '../../utils/studioNotifyChannels';

export interface NotifyChannelPromptProps {
  title?: string;
  detail?: string;
  /** When true, email already ran — do not offer Email again. */
  emailAlreadyQueued?: boolean;
  emailStatus?: string;
  preference?: ClientPreference;
  hasEmail?: boolean;
  hasPhone?: boolean;
  allowSkip?: boolean;
  whatsAppUrl?: string;
  onEmail?: () => Promise<{ status?: string; error?: string } | void> | { status?: string; error?: string } | void;
  onDone?: (summary: { channel: NotifyChannel; emailStatus?: string; whatsAppOpened?: boolean; lines: string[] }) => void;
  onDismiss?: () => void;
}

const NotifyChannelPrompt: React.FC<NotifyChannelPromptProps> = ({
  title = 'How should we reach the client?',
  detail,
  emailAlreadyQueued = false,
  emailStatus,
  preference,
  hasEmail = true,
  hasPhone = false,
  allowSkip = true,
  whatsAppUrl = '',
  onEmail,
  onDone,
  onDismiss,
}) => {
  const initial = useMemo(
    () => defaultNotifyChannel(preference, { emailQueuedAlready: emailAlreadyQueued, hasEmail, hasPhone }),
    [preference, emailAlreadyQueued, hasEmail, hasPhone],
  );
  const [channel, setChannel] = useState<NotifyChannel>(initial);
  const [busy, setBusy] = useState(false);
  const [lines, setLines] = useState<string[]>([]);
  const [error, setError] = useState('');

  const emailOptionDisabled = emailAlreadyQueued || !hasEmail;
  const whatsAppDisabled = !hasPhone || !whatsAppUrl;

  const sendOnWhatsApp = () => {
    const handoff = openWhatsAppHandoff(whatsAppUrl);
    onDone?.({
      channel: 'whatsapp',
      emailStatus,
      whatsAppOpened: handoff.opened,
      lines: [handoff.message],
    });
  };

  if (emailAlreadyQueued) {
    return (
      <aside className="studio-panel studio-notify-prompt" data-testid="notify-channel-prompt" aria-label="Client communication">
        <p className="studio-note" data-testid="email-already-queued">
          You can also send this on WhatsApp.
        </p>
        <div className="studio-toolbar">
          <button type="button" className="studio-primary" disabled={whatsAppDisabled} onClick={sendOnWhatsApp}>
            Send on WhatsApp
          </button>
          {onDismiss ? (
            <button type="button" className="studio-text-action" onClick={onDismiss}>
              Done
            </button>
          ) : null}
        </div>
      </aside>
    );
  }

  const run = async () => {
    setBusy(true);
    setError('');
    const resultLines: string[] = [];
    let nextEmailStatus = emailStatus;
    let whatsAppOpened = false;

    try {
      if (emailAlreadyQueued && emailStatus) {
        resultLines.push(`Email already handled — ${describeEmailStatus(emailStatus)}`);
      }

      const wantsEmail = channel === 'email' || channel === 'both';
      const wantsWhatsApp = channel === 'whatsapp' || channel === 'both';

      if (wantsEmail && !emailAlreadyQueued) {
        if (!onEmail) {
          resultLines.push('Email was not available for this action.');
        } else {
          const outcome = await onEmail();
          nextEmailStatus = outcome?.status || nextEmailStatus;
          if (outcome?.error) resultLines.push(`Email failed: ${outcome.error}`);
          else resultLines.push(describeEmailStatus(outcome?.status) || 'Email step finished.');
        }
      }

      if (wantsWhatsApp) {
        const handoff = openWhatsAppHandoff(whatsAppUrl);
        whatsAppOpened = handoff.opened;
        resultLines.push(handoff.message);
      }

      if (channel === 'skip') {
        resultLines.push('Communication skipped.');
      }

      setLines(resultLines);
      onDone?.({ channel, emailStatus: nextEmailStatus, whatsAppOpened, lines: resultLines });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Communication could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="studio-panel studio-notify-prompt" data-testid="notify-channel-prompt" aria-label="Client communication">
      <h3 className="studio-section-label">{title}</h3>
      {detail ? <p className="studio-note">{detail}</p> : null}
      {emailAlreadyQueued ? (
        <p className="studio-note" data-testid="email-already-queued">
          Email has already been queued for this change. Do not send the same email again.
          {emailStatus ? ` (${describeEmailStatus(emailStatus)})` : ''}
        </p>
      ) : null}
      <div className="studio-view-tabs" role="radiogroup" aria-label="Communication channel">
        {!emailAlreadyQueued ? (
          <button
            type="button"
            className={channel === 'email' ? 'is-active' : ''}
            aria-pressed={channel === 'email'}
            disabled={emailOptionDisabled}
            onClick={() => setChannel('email')}
          >
            Email
          </button>
        ) : null}
        <button
          type="button"
          className={channel === 'whatsapp' ? 'is-active' : ''}
          aria-pressed={channel === 'whatsapp'}
          disabled={whatsAppDisabled}
          onClick={() => setChannel('whatsapp')}
        >
          WhatsApp
        </button>
        {!emailAlreadyQueued ? (
          <button
            type="button"
            className={channel === 'both' ? 'is-active' : ''}
            aria-pressed={channel === 'both'}
            disabled={emailOptionDisabled || whatsAppDisabled}
            onClick={() => setChannel('both')}
          >
            Both
          </button>
        ) : null}
        {allowSkip ? (
          <button
            type="button"
            className={channel === 'skip' ? 'is-active' : ''}
            aria-pressed={channel === 'skip'}
            onClick={() => setChannel('skip')}
          >
            Skip
          </button>
        ) : null}
      </div>
      <p className="studio-note">
        WhatsApp opens for you to review and send. The studio does not claim WhatsApp delivery.
        Apple Mail and WhatsApp are not synchronised here.
      </p>
      {error ? <p className="studio-error" role="alert">{error}</p> : null}
      {lines.length > 0 ? (
        <div data-testid="notify-channel-result">
          {lines.map((line) => <p className="studio-note" key={line}>{line}</p>)}
        </div>
      ) : null}
      <div className="studio-toolbar">
        <button type="button" className="studio-primary" disabled={busy} onClick={run} data-testid="notify-channel-go">
          {busy ? 'Working…' : 'Continue'}
        </button>
        {onDismiss ? (
          <button type="button" className="studio-text-action" disabled={busy} onClick={onDismiss}>
            Close
          </button>
        ) : null}
      </div>
    </aside>
  );
};

export default NotifyChannelPrompt;
